import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Virtuoso } from 'react-virtuoso'
import {
    closestCenter,
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Box } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useGetMiscItemsQuery, useUpdateMiscItemsMutation } from '@/app/services/miscItems'
import { useCalculateQuoteMutation, useGetQuoteQuery, useUpdateQuoteMutation } from '@/app/services/quotes'
import { selectMiscFilterOptions } from '@/app/slices/miscItemsSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { setIsCalculatingQuote } from '@/app/slices/quoteItemsSlice'
import { QUOTE_STATUS_ARRAY } from '@/common/utils'

import MiscItem from './MiscItemContent/MiscItem'
import MiscellaneousItemOverlay from './MiscellaneousItemOverlay'
import SeekMiscItemOverlay from './SeekMiscItemOverlay'

/**
 * Custom List component for Virtuoso with flex layout styling.
 * Extracted outside MiscellaneousItemList to avoid re-creation on each render (React 19 ref as prop).
 *
 * @param {Object} props - Component props
 * @param {React.Ref} [props.ref] - Ref forwarded by Virtuoso
 * @param {Object|Array} [props.sx] - MUI sx prop for additional styling
 * @returns {React.ReactElement} A Box component configured as a flex container
 */
const MiscVirtuosoList = ({ ref = null, sx = null, ...rest }) => {
    return (
        <Box
            {...rest}
            ref={ref}
            sx={[
                {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        />
    )
}

MiscVirtuosoList.propTypes = {
    ref: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
    sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
}

/**
 * Renders the list of miscellaneous items for a quote with virtualization and drag-and-drop support.
 *
 * Uses react-virtuoso for efficient rendering of large lists, with scroll-seek placeholders
 * for smooth scrolling performance. Supports drag-and-drop reordering via @dnd-kit.
 *
 * @param {Object} props
 * @param {boolean} [props.showAttachments] - Shows each item's documents control; read once by the quote screen
 * @returns {React.ReactElement|null} The virtualized misc items list or null while loading
 */
const MiscellaneousItemList = ({ showAttachments = false }) => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const dispatch = useDispatch()
    const { quoteId } = useParams()

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    const organisationId = useSelector(selectOrganisationId)

    const filterOptions = useSelector(selectMiscFilterOptions)
    const { data: selectedQuote } = useGetQuoteQuery({ organisationId, quoteId })
    const {
        data: miscItems,
        isFetching: isFetchingMiscItems,
        isLoading: isLoadingMiscitems,
    } = useGetMiscItemsQuery({
        organisationId,
        quoteId,
    })

    const miscItemsIds = useMemo(() => {
        if (isLoadingMiscitems) return []
        return miscItems.map((item) => item?.id)
    }, [miscItems, isLoadingMiscitems])

    const [calculateQuoteMutation, { isLoading: isCalculating }] = useCalculateQuoteMutation({
        fixedCacheKey: 'shared-calculate-quote',
    })

    const [updateMiscItems] = useUpdateMiscItemsMutation()
    const [updateQuote] = useUpdateQuoteMutation()

    const [activeItemId, setActiveItemId] = useState(null)
    const [activeItemName, setActiveItemName] = useState(null)

    const onDragStart = (event) => {
        const id = event.active.id
        setActiveItemId(id)

        const activeItem = miscItems.find((item) => item?.id === id)
        setActiveItemName(activeItem.name)
    }

    const handleUpdateQuote = useCallback(
        async (updatedFields) => {
            try {
                await updateQuote({
                    organisationId,
                    quoteId,
                    quote: {
                        ...selectedQuote,
                        ...updatedFields,
                    },
                }).unwrap()
            } catch {
                const errorMessage = t('$t(An error occurred) updating the $t(quote).')
                enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 5000 })
            }
        },
        [updateQuote, organisationId, quoteId, selectedQuote, enqueueSnackbar, t]
    )

    const handleUpdateMiscItems = useCallback(
        async (sortedList) => {
            try {
                await updateMiscItems({
                    organisationId,
                    quoteId,
                    miscItems: sortedList,
                }).unwrap()
            } catch {
                enqueueSnackbar(t('Failed to reorder $t(quote) items. Undoing changes'), {
                    variant: 'error',
                    autoHideDuration: 5000,
                })
            } finally {
                setActiveItemId(null)
            }
        },
        [updateMiscItems, organisationId, quoteId, enqueueSnackbar, t]
    )

    const onDragEnd = useCallback(
        (event) => {
            const { active, over } = event

            if (!active || !over) {
                return
            }

            if (active.id === over.id) {
                return
            }

            const oldIndex = active.data.current.sortable.index
            const newIndex = over.data.current.sortable.index

            const items = structuredClone(arrayMove(miscItems, oldIndex, newIndex))

            items?.forEach((item, index) => {
                item.index = index
            })

            if (selectedQuote.sortMiscItemsBy !== 'Custom') {
                handleUpdateQuote({ sortMiscItemsBy: 'Custom', sortMiscItemsAsc: true })
            }

            handleUpdateMiscItems(items)
        },
        [miscItems, handleUpdateMiscItems, selectedQuote, handleUpdateQuote]
    )

    const filteredMiscItems = useMemo(() => {
        if (filterOptions === 'showAllMiscItems') {
            return miscItems
        }

        return miscItems.filter((_miscItem) => {
            return false
        })
    }, [miscItems, filterOptions])

    const calculateQuote = useCallback(async () => {
        try {
            await calculateQuoteMutation({ organisationId, quoteId }).unwrap()
        } catch (error) {
            const errorMessage = t(error.data)
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 5000 })
        }
    }, [calculateQuoteMutation, organisationId, quoteId, enqueueSnackbar, t])

    useEffect(() => {
        dispatch(setIsCalculatingQuote(isCalculating))
    }, [isCalculating, dispatch])

    useEffect(() => {
        if (organisationId && quoteId && [QUOTE_STATUS_ARRAY.Draft].includes(selectedQuote?.status)) {
            calculateQuote()
        }
    }, [organisationId, quoteId, selectedQuote?.status, calculateQuote])

    // Note: Initial loading is handled by parent Quote component
    // This component only renders when data is available
    // The opacity fade handles refetch scenarios to keep DOM stable
    return !isLoadingMiscitems ? (
        <Box sx={{ position: 'relative', opacity: isFetchingMiscItems ? 0.6 : 1, transition: 'opacity 0.2s' }}>
            <DndContext
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                sensors={sensors}
                onDragEnd={onDragEnd}
                onDragStart={onDragStart}
            >
                <SortableContext
                    items={miscItemsIds}
                    strategy={verticalListSortingStrategy}
                >
                    <Virtuoso
                        components={{
                            List: MiscVirtuosoList,
                            ScrollSeekPlaceholder: SeekMiscItemOverlay,
                        }}
                        data={filteredMiscItems}
                        defaultItemHeight={200}
                        increaseViewportBy={300}
                        itemContent={(index, miscItem) => (
                            <MiscItem
                                id={miscItem?.id}
                                key={miscItem?.id}
                                showAttachments={showAttachments}
                            />
                        )}
                        scrollSeekConfiguration={{
                            enter: (velocity) => Math.abs(velocity) > 600,
                            exit: (velocity) => Math.abs(velocity) < 100,
                        }}
                        useWindowScroll
                    />
                </SortableContext>
                <DragOverlay>
                    {activeItemId ? (
                        <MiscellaneousItemOverlay
                            id={activeItemId}
                            name={activeItemName}
                        />
                    ) : null}
                </DragOverlay>
            </DndContext>
        </Box>
    ) : null
}

MiscellaneousItemList.propTypes = {
    showAttachments: PropTypes.bool,
}

export default memo(MiscellaneousItemList)
